// SCR-STAFF-706 — تنظیمات MED (US-MED-002، 003، 041، 042): فهرست داروی مستأجر (اقلام اختصاصی، داروی کنترل‌شده با سقف روز، پیوند انبار)، نسخه‌های
// آماده مستأجر، پنل‌های آزمایش با دستور آمادگی مشترک و مراکز طرف قرارداد (نوع، تماس، روش ارسال و دریافت نتیجه، قرارداد).
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import FormControlLabel from '@mui/material/FormControlLabel';
import MenuItem from '@mui/material/MenuItem';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import TextField from '@mui/material/TextField';
import {
  unwrap,
  type MedOrderPanelView,
  type MedOrderType,
  type MedPartnerResultMethod,
  type MedPartnerSendMethod,
  type MedPartnerType,
  type MedPartnerView,
  type MedPrescriptionItemKind,
  type MedRxTemplateView,
  type MedTenantDrugView,
} from '@phoenix/api-client';
import { useAccess, useIdempotencyKey, useTenantSession } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import { DataTable, FormDialog, ListPage, SampleBadge, type DataTableColumn } from '@phoenix/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { InsurerCatalogSection } from './InsurerCatalogSection';
import { ReferrersSettingsPanel } from './Phase2Panels';
import { medKeys, SCR_STAFF_706 } from './common';

type Section = 'drugs' | 'templates' | 'panels' | 'partners' | 'referrers' | 'insurer_catalog';
const KINDS: readonly MedPrescriptionItemKind[] = ['Drug', 'MedicalSupply', 'Cosmeceutical'];
const ORDER_TYPES: readonly MedOrderType[] = ['Lab', 'Imaging', 'Other'];
const PARTNER_TYPES: readonly MedPartnerType[] = ['Laboratory', 'Imaging', 'Both'];
const SEND_METHODS: readonly MedPartnerSendMethod[] = ['Print', 'Electronic'];
const RESULT_METHODS: readonly MedPartnerResultMethod[] = ['Manual', 'Pdf', 'Hl7Fhir'];

type Dialog = { kind: 'drug' } | { kind: 'panel' } | { kind: 'partner'; partner?: MedPartnerView } | null;

export function MedSettingsPage() {
  const { t } = useTranslation('med');
  const { tenantApi, session } = useTenantSession();
  const access = useAccess();
  const queryClient = useQueryClient();
  const key = useIdempotencyKey();
  const tenantId = session?.tenantId;
  const full = access.accessMode === 'Full';
  const sections: Section[] = [
    ...(access.can('med.drug_list.manage') ? (['drugs', 'templates'] as const) : []),
    ...(access.can('med.order_panel.manage') ? (['panels'] as const) : []),
    ...(access.can('med.partner.manage') ? (['partners'] as const) : []),
    // AP-MED-02 (US-MED-092): داده پایه بیمه‌گر و نگاشت کد.
    ...(access.can('med.insurer_catalog.manage') ? (['insurer_catalog'] as const) : []),
    // AP-CLN-04 (BR-MED-061؛ US-MED-063، 064): ارجاع‌دهندگان و مقاصد بیرونی.
    ...(access.can('med.partner.manage') || access.can('med.referral.schedule') ? (['referrers'] as const) : []),
  ];
  const [section, setSection] = useState<Section>(sections[0] ?? 'drugs');
  const [dialog, setDialog] = useState<Dialog>(null);
  const [form, setForm] = useState<Record<string, string>>({});
  const [flag, setFlag] = useState(false);
  const set = (name: string, value: string) => setForm((current) => ({ ...current, [name]: value }));
  const text = (name: string) => (form[name] ?? '').trim() || null;

  const drugs = useQuery({ queryKey: medKeys.drugList(tenantId), enabled: section === 'drugs', queryFn: () => unwrap(tenantApi.GET('/api/v1/medical-orders/settings/drug-list')) });
  const templates = useQuery({ queryKey: medKeys.templates(tenantId), enabled: section === 'templates', queryFn: () => unwrap(tenantApi.GET('/api/v1/medical-orders/rx-templates')) });
  const panels = useQuery({ queryKey: medKeys.panels(tenantId), enabled: section === 'panels', queryFn: () => unwrap(tenantApi.GET('/api/v1/medical-orders/settings/order-panels')) });
  const partners = useQuery({ queryKey: medKeys.partners(tenantId), enabled: section === 'partners', queryFn: () => unwrap(tenantApi.GET('/api/v1/medical-orders/settings/partners')) });

  const open = (next: Dialog, initial: Record<string, string> = {}, initialFlag = false) => {
    setForm(initial);
    setFlag(initialFlag);
    setDialog(next);
  };
  const save = useMutation({
    mutationFn: async () => {
      const headers = { 'Idempotency-Key': key.current() };
      if (dialog?.kind === 'drug') {
        return unwrap(
          tenantApi.POST('/api/v1/medical-orders/settings/drug-list', {
            headers,
            body: {
              drugCode: text('drugCode'),
              name: text('name'),
              nameEn: null,
              form: text('form'),
              strength: text('strength'),
              kind: (form.kind ?? 'Drug') as MedPrescriptionItemKind,
              isControlled: flag,
              controlledClass: null,
              therapeuticClass: null,
              ingredients: [],
              controlledMaxDays: flag && text('maxDays') ? Number(text('maxDays')) : null,
              inventoryItemId: text('inventoryItemId'),
              isActive: true,
            },
          }),
        );
      }
      if (dialog?.kind === 'panel') {
        const items = (form.items ?? '').split(/[,\u060C]/).map((value) => value.trim()).filter(Boolean).map((code) => ({ code: code.toUpperCase(), name: code, repeatCount: 1 }));
        return unwrap(tenantApi.POST('/api/v1/medical-orders/settings/order-panels', { headers, body: { name: text('name') ?? '', type: (form.type ?? 'Lab') as MedOrderType, items, preparation: text('preparation'), scope: 'Tenant', isActive: true } }));
      }
      if (dialog?.kind === 'partner') {
        const body = {
          name: text('name') ?? '',
          type: (form.type ?? 'Laboratory') as MedPartnerType,
          address: text('address'),
          phone: text('phone'),
          sendMethod: (form.sendMethod ?? 'Print') as MedPartnerSendMethod,
          resultMethod: (form.resultMethod ?? 'Manual') as MedPartnerResultMethod,
          contractNote: text('contractNote'),
          isActive: flag,
        };
        return dialog.partner
          ? unwrap(tenantApi.PUT('/api/v1/medical-orders/settings/partners/{id}', { params: { path: { id: dialog.partner.id } }, body }))
          : unwrap(tenantApi.POST('/api/v1/medical-orders/settings/partners', { headers, body }));
      }
      return null;
    },
    onSettled: (_data, error) => key.settle(error ?? undefined),
    onSuccess: () => {
      setDialog(null);
      void queryClient.invalidateQueries({ queryKey: medKeys.all(tenantId) });
    },
  });
  const removeTemplate = useMutation({
    mutationFn: (id: string) => tenantApi.DELETE('/api/v1/medical-orders/settings/rx-templates/{id}', { params: { path: { id } } }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: medKeys.templates(tenantId) }),
  });

  const drugColumns: DataTableColumn<MedTenantDrugView>[] = [
    { id: 'code', headerKey: 'med:settings.code', cell: (row) => <bdi dir="ltr">{row.code}</bdi> },
    { id: 'name', headerKey: 'med:settings.name', cell: (row) => <span>{[row.drug.genericName, row.drug.strength, row.drug.form].filter(Boolean).join(' ')} {row.drug.isSample ? <SampleBadge /> : null}</span> },
    { id: 'kind', headerKey: 'med:settings.kind', cell: (row) => t(`med:item_kind.${row.drug.kind}`) },
    { id: 'controlled', headerKey: 'med:rx.controlled', cell: (row) => (row.drug.isControlled ? <Chip size="small" color="secondary" label={t('med:settings.max_days_value', { days: Number(row.drug.controlledMaxDays) })} /> : null) },
    { id: 'stock', headerKey: 'med:settings.inventory', cell: (row) => (row.drug.availableInCenter ? t('med:drug.available') : '—') },
  ];
  const templateColumns: DataTableColumn<MedRxTemplateView>[] = [
    { id: 'name', headerKey: 'med:settings.name', cell: (row) => row.name },
    { id: 'scope', headerKey: 'med:settings.scope', cell: (row) => t(`med:scope.${row.scope}`) },
    { id: 'items', headerKey: 'med:rx.items', cell: (row) => row.items.map((item) => item.name ?? item.drugCode).join(t('med:common.list_separator')) },
    {
      id: 'actions',
      headerKey: 'med:common.actions',
      cell: (row) =>
        full && row.scope === 'Tenant' ? (
          <Button size="small" color="error" onClick={() => removeTemplate.mutate(row.id)}>
            {t('med:settings.remove')}
          </Button>
        ) : null,
    },
  ];
  const panelColumns: DataTableColumn<MedOrderPanelView>[] = [
    { id: 'name', headerKey: 'med:settings.name', cell: (row) => row.name },
    { id: 'type', headerKey: 'med:order.type', cell: (row) => t(`med:order_type.${row.type}`) },
    { id: 'items', headerKey: 'med:order.items', cell: (row) => row.items.map((item) => item.code).join(t('med:common.list_separator')) },
    { id: 'preparation', headerKey: 'med:order.preparation', cell: (row) => row.preparation ?? '—' },
    { id: 'scope', headerKey: 'med:settings.scope', cell: (row) => t(`med:scope.${row.scope}`) },
  ];
  const partnerColumns: DataTableColumn<MedPartnerView>[] = [
    { id: 'name', headerKey: 'med:settings.name', cell: (row) => row.name },
    { id: 'type', headerKey: 'med:settings.kind', cell: (row) => t(`med:partner_type.${row.type}`) },
    { id: 'phone', headerKey: 'med:settings.phone', cell: (row) => <bdi dir="ltr">{row.phone ?? '—'}</bdi> },
    { id: 'methods', headerKey: 'med:settings.methods', cell: (row) => `${t(`med:send_method.${row.sendMethod}`)} · ${t(`med:result_method.${row.resultMethod}`)}` },
    { id: 'active', headerKey: 'med:common.status', cell: (row) => (row.isActive ? t('med:settings.active') : t('med:settings.inactive')) },
    {
      id: 'actions',
      headerKey: 'med:common.actions',
      cell: (row) =>
        full ? (
          <Button
            size="small"
            onClick={() => open({ kind: 'partner', partner: row }, { name: row.name, type: row.type, address: row.address ?? '', phone: row.phone ?? '', sendMethod: row.sendMethod, resultMethod: row.resultMethod, contractNote: row.contractNote ?? '' }, row.isActive)}
            data-testid="med-partner-edit"
          >
            {t('med:settings.edit')}
          </Button>
        ) : null,
    },
  ];
  const select = (name: string, values: readonly string[], prefix: string, fallback: string) => (
    <TextField select label={t(`med:settings.${name}`)} value={form[name] ?? fallback} onChange={(event) => set(name, event.target.value)} slotProps={{ htmlInput: { 'data-testid': `med-settings-${name}` } }}>
      {values.map((value) => (
        <MenuItem key={value} value={value}>
          {t(`${prefix}.${value}`)}
        </MenuItem>
      ))}
    </TextField>
  );
  const input = (name: string, required = false) => (
    <TextField label={t(`med:settings.${name}`)} required={required} value={form[name] ?? ''} onChange={(event) => set(name, event.target.value)} slotProps={{ htmlInput: { maxLength: 500, 'data-testid': `med-settings-${name}` } }} />
  );

  return (
    <ListPage
      screenId={SCR_STAFF_706}
      titleKey="med:settings.title"
      subtitleKey="med:settings.subtitle"
      actions={
        full && section !== 'templates' && section !== 'referrers' && section !== 'insurer_catalog' ? (
          <Button variant="contained" onClick={() => open(section === 'drugs' ? { kind: 'drug' } : section === 'panels' ? { kind: 'panel' } : { kind: 'partner' }, {}, section === 'partners')} data-testid="med-settings-add">
            {t(`med:settings.add_${section}`)}
          </Button>
        ) : undefined
      }
    >
      <Tabs value={section} onChange={(_, next: Section) => setSection(next)} sx={{ mb: 1.5 }} aria-label={t('med:settings.title')}>
        {sections.map((item) => (
          <Tab key={item} value={item} label={t(`med:settings.${item}`)} data-testid={`med-settings-tab-${item}`} />
        ))}
      </Tabs>
      {section === 'drugs' && <DataTable captionKey="med:settings.drugs" columns={drugColumns} rows={drugs.data?.items} getRowId={(row) => row.id} isLoading={drugs.isPending} error={drugs.error} onRetry={() => void drugs.refetch()} emptyTitleKey="med:settings.empty" />}
      {section === 'templates' && <DataTable captionKey="med:settings.templates" columns={templateColumns} rows={templates.data?.items} getRowId={(row) => row.id} isLoading={templates.isPending} error={templates.error} onRetry={() => void templates.refetch()} emptyTitleKey="med:settings.empty" />}
      {section === 'panels' && <DataTable captionKey="med:settings.panels" columns={panelColumns} rows={panels.data?.items} getRowId={(row) => row.id} isLoading={panels.isPending} error={panels.error} onRetry={() => void panels.refetch()} emptyTitleKey="med:settings.empty" />}
      {section === 'referrers' && <ReferrersSettingsPanel />}
      {section === 'insurer_catalog' && <InsurerCatalogSection />}
      {section === 'partners' && <DataTable captionKey="med:settings.partners" columns={partnerColumns} rows={partners.data?.items} getRowId={(row) => row.id} isLoading={partners.isPending} error={partners.error} onRetry={() => void partners.refetch()} emptyTitleKey="med:settings.empty" />}
      {dialog && (
        <FormDialog
          open
          titleKey={dialog.kind === 'drug' ? 'med:settings.add_drugs' : dialog.kind === 'panel' ? 'med:settings.add_panels' : 'med:settings.add_partners'}
          onSubmit={() => save.mutate()}
          onClose={() => setDialog(null)}
          isDirty={Object.keys(form).length > 0}
          isSubmitting={save.isPending}
          submitError={save.error}
        >
          {dialog.kind === 'drug' && (
            <>
              {input('drugCode')}
              {input('name')}
              {input('form')}
              {input('strength')}
              {select('kind', KINDS, 'med:item_kind', 'Drug')}
              <FormControlLabel control={<Checkbox checked={flag} onChange={(event) => setFlag(event.target.checked)} />} label={t('med:rx.controlled')} />
              {flag && input('maxDays')}
              {input('inventoryItemId')}
            </>
          )}
          {dialog.kind === 'panel' && (
            <>
              {input('name', true)}
              {select('type', ORDER_TYPES, 'med:order_type', 'Lab')}
              {input('items', true)}
              {input('preparation')}
            </>
          )}
          {dialog.kind === 'partner' && (
            <>
              {input('name', true)}
              {select('type', PARTNER_TYPES, 'med:partner_type', 'Laboratory')}
              {input('address')}
              {input('phone')}
              {select('sendMethod', SEND_METHODS, 'med:send_method', 'Print')}
              {select('resultMethod', RESULT_METHODS, 'med:result_method', 'Manual')}
              {input('contractNote')}
              <FormControlLabel control={<Checkbox checked={flag} onChange={(event) => setFlag(event.target.checked)} />} label={t('med:settings.active')} />
            </>
          )}
        </FormDialog>
      )}
    </ListPage>
  );
}
